/**
 * GamificationPanel Component
 * Smart Purchase Planner 2.0 - Phase 4 UI Components
 * 
 * Displays XP, level, badges, streak, and shopping scores
 */

import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy,
  Flame,
  Star,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Zap,
  Award,
  Target,
  TrendingUp
} from 'lucide-react';
import {
  getLevelFromXP,
  calculateStreak,
  getEarnedBadges,
  getBadgeProgress,
  BADGES
} from '@/services/shoppingGamification';
import Tooltip from '@/components/common/Tooltip';

/**
 * Main Gamification Panel
 * Shows XP, level progress, streak, and badges
 */
export default function GamificationPanel({
  totalXP = 0,
  shoppingHistory = [],
  earnedBadgeIds = [],
  compact = false
}) {
  const [expanded, setExpanded] = useState(!compact);
  const [showAllBadges, setShowAllBadges] = useState(false);
  
  const levelInfo = useMemo(() => getLevelFromXP(totalXP), [totalXP]);
  const streakInfo = useMemo(() => calculateStreak(shoppingHistory), [shoppingHistory]);
  const earnedBadges = useMemo(() => getEarnedBadges(earnedBadgeIds), [earnedBadgeIds]);
  
  // Compact mode - show badge, and full panel when expanded
  if (compact && !expanded) {
    return (
      <CompactGamificationBadge
        level={levelInfo.level}
        levelName={levelInfo.name}
        xp={totalXP}
        streak={streakInfo.currentStreak}
        onClick={() => setExpanded(true)}
      />
    );
  }
  
  return (
    <div className="bg-gradient-to-br from-violet-50 via-white to-indigo-50 dark:from-violet-900/30 dark:via-slate-800 dark:to-indigo-900/30 border-2 border-violet-200 dark:border-violet-800 rounded-2xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-violet-50/50 dark:hover:bg-violet-900/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg">
            <Trophy size={24} className="text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-gray-900 dark:text-white">Level {levelInfo.level}</h3>
            <p className="text-sm text-violet-600 dark:text-violet-400 font-medium">{levelInfo.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {streakInfo.currentStreak > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 dark:bg-orange-900/50 rounded-full">
              <Flame size={14} className="text-orange-500" />
              <span className="text-sm font-bold text-orange-600 dark:text-orange-400">{streakInfo.currentStreak}</span>
            </div>
          )}
          <div className="flex items-center gap-1 px-2 py-1 bg-violet-100 dark:bg-violet-900/50 rounded-full">
            <Zap size={14} className="text-violet-500" />
            <span className="text-sm font-bold text-violet-600 dark:text-violet-400">{totalXP} XP</span>
          </div>
          {compact ? (
            <span className="text-xs text-gray-400">✕ close</span>
          ) : expanded ? (
            <ChevronUp size={20} className="text-gray-400" />
          ) : (
            <ChevronDown size={20} className="text-gray-400" />
          )}
        </div>
      </button>
      
      {/* Expanded Content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-violet-100 dark:border-violet-800"
          >
            <div className="p-4 space-y-4">
              {/* XP Progress Bar */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600 dark:text-gray-400">Progress to Level {levelInfo.level + 1}</span>
                  <span className="font-mono text-violet-600 dark:text-violet-400">
                    {levelInfo.xpInLevel} / {levelInfo.xpToNextLevel + levelInfo.xpInLevel} XP
                  </span>
                </div>
                <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${levelInfo.progress}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full"
                  />
                </div>
                {levelInfo.nextLevelName && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {levelInfo.xpToNextLevel} XP to become a {levelInfo.nextLevelName}
                  </p>
                )}
              </div>
              
              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-3">
                <StatCard
                  icon={<Flame className="text-orange-500" />}
                  label="Current Streak"
                  value={streakInfo.currentStreak}
                  suffix=" days"
                  color="orange"
                />
                <StatCard
                  icon={<TrendingUp className="text-emerald-500" />}
                  label="Best Streak"
                  value={streakInfo.longestStreak}
                  suffix=" days"
                  color="emerald"
                />
                <StatCard
                  icon={<Award className="text-violet-500" />}
                  label="Badges"
                  value={earnedBadges.length}
                  suffix={`/${Object.keys(BADGES).length}`}
                  color="violet"
                />
              </div>
              
              {/* Badges Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Earned Badges</h4>
                  <button
                    onClick={() => setShowAllBadges(!showAllBadges)}
                    className="text-xs text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 font-medium"
                  >
                    {showAllBadges ? 'Show Less' : 'View All'}
                  </button>
                </div>
                
                {earnedBadges.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {(showAllBadges ? earnedBadges : earnedBadges.slice(0, 6)).map((badge) => (
                      <BadgeChip key={badge.id} badge={badge} earned />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                    Complete shopping trips to earn your first badge! 🏆
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Small stat card component
 */
function StatCard({ icon, label, value, suffix = '', color = 'gray' }) {
  const colorClasses = {
    orange: 'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-700',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700',
    violet: 'bg-violet-50 dark:bg-violet-900/30 border-violet-200 dark:border-violet-700',
    gray: 'bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600'
  };
  
  return (
    <div className={`p-3 rounded-xl border ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        {React.cloneElement(icon, { size: 14 })}
        <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
      </div>
      <p className="text-lg font-bold text-gray-900 dark:text-white">
        {value}{suffix}
      </p>
    </div>
  );
}

/**
 * Individual badge chip
 */
function BadgeChip({ badge, earned = false, showProgress = false, progress = 0 }) {
  return (
    <Tooltip label={`${badge.name}: ${badge.description}`}>
      <div
        className={`
          inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm
          ${earned 
            ? 'bg-gradient-to-r from-amber-100 to-yellow-100 dark:from-amber-900/50 dark:to-yellow-900/50 border border-amber-300 dark:border-amber-700 shadow-sm' 
            : 'bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 opacity-60'
          }
        `}
      >
        <span className="text-lg">{badge.emoji}</span>
        {showProgress && !earned && (
          <span className="text-xs text-gray-500 dark:text-gray-400">{progress}%</span>
        )}
      </div>
    </Tooltip>
  );
}

/**
 * Compact gamification badge for inline display
 */
function CompactGamificationBadge({ level, levelName, xp, streak, onClick }) {
  const tooltipText = `🏆 Level ${level}: ${levelName} | ⚡ ${xp} XP earned from shopping. Earn XP by completing trips, staying on budget, and building streaks! Click to see your progress & badges.`;
  
  return (
    <button
      onClick={onClick}
      title={tooltipText}
      className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-violet-100 to-indigo-100 dark:from-violet-900/50 dark:to-indigo-900/50 border border-violet-200 dark:border-violet-700 rounded-full hover:shadow-md hover:scale-105 transition-all cursor-pointer"
    >
      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
        <span className="text-xs font-bold text-white">{level}</span>
      </div>
      <span className="text-sm font-medium text-violet-700 dark:text-violet-300">{xp} XP</span>
      {streak > 0 && (
        <span className="flex items-center gap-0.5 text-orange-600 dark:text-orange-400">
          <Flame size={12} />
          <span className="text-xs font-bold">{streak}</span>
        </span>
      )}
    </button>
  );
}

/**
 * Trip Score Display
 * Shows the score breakdown after completing a shopping trip
 */
export function TripScoreCard({ score, grade, breakdown = [], xpEarned = 0, newBadges = [] }) {
  const gradeColors = {
    'S': 'from-amber-400 to-yellow-500 text-amber-900',
    'A+': 'from-emerald-400 to-green-500 text-emerald-900',
    'A': 'from-emerald-400 to-green-500 text-emerald-900',
    'B+': 'from-blue-400 to-cyan-500 text-blue-900',
    'B': 'from-blue-400 to-cyan-500 text-blue-900',
    'C+': 'from-gray-400 to-slate-500 text-gray-900',
    'C': 'from-gray-400 to-slate-500 text-gray-900',
    'D': 'from-orange-400 to-amber-500 text-orange-900',
    'F': 'from-red-400 to-rose-500 text-red-900'
  };
  
  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-600 rounded-2xl overflow-hidden shadow-lg"
    >
      {/* Grade Header */}
      <div className={`p-6 bg-gradient-to-r ${gradeColors[grade] || gradeColors['C']} text-center`}>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
          className="text-6xl font-black mb-2"
        >
          {grade}
        </motion.div>
        <p className="text-lg font-semibold opacity-80">Trip Score: {score}/100</p>
      </div>
      
      {/* Score Breakdown */}
      <div className="p-4 space-y-3">
        <h4 className="text-sm font-semibold text-gray-700">Score Breakdown</h4>
        {breakdown.map((item, idx) => (
          <motion.div
            key={idx}
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.1 * idx }}
            className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">{item.emoji}</span>
              <span className="text-sm text-gray-700">{item.label}</span>
            </div>
            <span className="text-sm font-bold text-violet-600">+{item.points}</span>
          </motion.div>
        ))}
        
        {/* XP Earned */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <Zap size={18} className="text-violet-500" />
            <span className="font-semibold text-gray-800">XP Earned</span>
          </div>
          <span className="text-lg font-bold text-violet-600">+{xpEarned}</span>
        </div>
        
        {/* New Badges */}
        {newBadges.length > 0 && (
          <div className="pt-3">
            <p className="text-sm font-semibold text-gray-700 mb-2">🎉 New Badges!</p>
            <div className="flex flex-wrap gap-2">
              {newBadges.map((badge) => (
                <motion.div
                  key={badge.id}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring' }}
                >
                  <BadgeChip badge={badge} earned />
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

/**
 * Streak Banner - Shows current streak prominently
 */
export function StreakBanner({ currentStreak, isActive = true }) {
  if (currentStreak === 0) return null;
  
  const getMessage = () => {
    if (currentStreak >= 30) return "Legendary streak! You're unstoppable! 🌟";
    if (currentStreak >= 14) return "Two weeks strong! Amazing discipline! 💪";
    if (currentStreak >= 7) return "One week streak! Keep it going! 🔥";
    if (currentStreak >= 3) return "Great start! Build that habit! ⭐";
    return "Streak started! Stay under budget tomorrow! 🎯";
  };
  
  return (
    <motion.div
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={`
        flex items-center gap-3 p-3 rounded-xl
        ${isActive 
          ? 'bg-gradient-to-r from-orange-100 to-amber-100 border border-orange-200' 
          : 'bg-gray-100 border border-gray-200 opacity-60'
        }
      `}
    >
      <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center">
        <Flame size={20} className="text-white" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-black text-orange-600">{currentStreak}</span>
          <span className="text-sm font-semibold text-orange-700">Day Streak!</span>
        </div>
        <p className="text-xs text-orange-600">{getMessage()}</p>
      </div>
    </motion.div>
  );
}
